import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, FileText, History, RefreshCw } from 'lucide-react';

import Topbar from '../main/Topbar';
import ProjectSidebar from '../../components/ProjectSidebar';
import { useBoardMenu } from '../../context/BoardMenuContext';
import { ProjectDetail } from '../../utils/commonUtils';
import { ShopFormUtils } from '../../utils/shopCommon';
import { parseBoardSettings } from '../../utils/board';
import { getPostMenuId, isQnaBoardCode } from '../../utils/boardMenu';
import { formatDateTimeFromApi } from '../../utils/dateTime';
import { getPost, getPostViewPath, updatePost, type BoardPost } from '../../utils/post';

interface FormErrors {
  title?: string;
  content?: string;
}

const FormRow: React.FC<{
  label: string;
  required?: boolean;
  hint?: string;
  children: React.ReactNode;
  error?: string;
}> = ({ label, required, hint, children, error }) => (
  <div className="flex flex-col sm:flex-row sm:items-start gap-3 py-3 border-b border-gray-100 last:border-0">
    <div className="w-52 sm:w-56 flex-shrink-0">
      <p className="text-sm font-medium text-gray-700">
        {label}
        {required && <span className="text-red-500 ml-0.5">*</span>}
      </p>
      {hint && <p className="mt-0.5 text-xs text-gray-500">{hint}</p>}
    </div>
    <div className="flex-1 min-w-0">
      {children}
      {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
    </div>
  </div>
);

const PostUpdate: React.FC = () => {
  const navigate = useNavigate();
  const {
    projectCode: projectCodeFromUrl,
    boardCode: boardCodeFromUrl,
    postCode: postCodeFromUrl,
  } = useParams<{
    projectCode: string;
    boardCode: string;
    postCode: string;
  }>();

  const { boards } = useBoardMenu();

  const [isDesktopSidebarOpen, setIsDesktopSidebarOpen] = useState(true);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);
  const [activeMenu, setActiveMenu] = useState('');

  const [projectDetail, setProjectDetail] = useState<ProjectDetail | null>(null);
  const [initialPost, setInitialPost] = useState<BoardPost | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [errors, setErrors] = useState<FormErrors>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const menuBoard = boards.find((b) => b.code === boardCodeFromUrl);
  const boardSettings = useMemo(
    () => parseBoardSettings(menuBoard?.settings),
    [menuBoard?.settings]
  );
  const fileUploadEnabled = boardSettings.upload > 0;

  const boardTitle = menuBoard?.name ?? boardCodeFromUrl ?? '';
  const viewPath =
    projectCodeFromUrl && boardCodeFromUrl && postCodeFromUrl
      ? getPostViewPath(projectCodeFromUrl, boardCodeFromUrl, postCodeFromUrl)
      : '/projects';

  const applyPostToForm = (post: BoardPost) => {
    setTitle(post.title ?? '');
    setContent(post.content ?? '');
  };

  ShopFormUtils.useMobileSidebarResize(setIsMobileSidebarOpen);

  useEffect(() => {
    if (!projectCodeFromUrl || !boardCodeFromUrl || !postCodeFromUrl) {
      navigate('/projects', { replace: true });
      return;
    }
    const projectInfo = ShopFormUtils.loadProjectDetail();
    if (!projectInfo) {
      navigate('/projects', { replace: true });
      return;
    }
    const detail: ProjectDetail = {
      id: (projectInfo as { id?: string }).id ?? projectInfo.code ?? '',
      ...projectInfo,
    };
    if (detail.code !== projectCodeFromUrl) {
      navigate('/projects', { replace: true });
      return;
    }
    setProjectDetail(detail);
    setActiveMenu(
      isQnaBoardCode(boardCodeFromUrl) ? 'qna' : getPostMenuId(boardCodeFromUrl)
    );
  }, [projectCodeFromUrl, boardCodeFromUrl, postCodeFromUrl, navigate]);

  useEffect(() => {
    if (!projectCodeFromUrl || !boardCodeFromUrl || !postCodeFromUrl) return;

    let cancelled = false;
    setIsLoading(true);
    setLoadError(null);

    getPost(projectCodeFromUrl, boardCodeFromUrl, postCodeFromUrl)
      .then((post) => {
        if (!cancelled) {
          setInitialPost(post);
          applyPostToForm(post);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setLoadError(err instanceof Error ? err.message : '게시물을 불러오지 못했습니다.');
          setInitialPost(null);
        }
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [projectCodeFromUrl, boardCodeFromUrl, postCodeFromUrl]);

  const validate = (): boolean => {
    const next: FormErrors = {};
    const trimmedTitle = title.trim();

    if (!trimmedTitle) next.title = '제목은 필수입니다.';
    else if (trimmedTitle.length > 100) next.title = '제목은 최대 100자까지 입력할 수 있습니다.';

    if (!content.trim()) next.content = '내용은 필수입니다.';

    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleReset = () => {
    if (initialPost) applyPostToForm(initialPost);
    setErrors({});
    setSubmitError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);
    if (!validate() || !projectCodeFromUrl || !boardCodeFromUrl || !postCodeFromUrl) return;

    try {
      setIsSubmitting(true);
      await updatePost(projectCodeFromUrl, boardCodeFromUrl, postCodeFromUrl, {
        title: title.trim(),
        content,
      });
      navigate(viewPath, { replace: true });
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : '게시물 수정에 실패했습니다.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!projectDetail) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <p className="text-gray-500">프로젝트 정보를 불러올 수 없습니다.</p>
          <button
            type="button"
            onClick={() => navigate('/projects')}
            className="mt-4 text-blue-600 hover:text-blue-700"
          >
            프로젝트 목록으로 돌아가기
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen bg-gray-50 flex overflow-hidden">
      <ProjectSidebar
        isDesktopSidebarOpen={isDesktopSidebarOpen}
        setIsDesktopSidebarOpen={setIsDesktopSidebarOpen}
        isMobileSidebarOpen={isMobileSidebarOpen}
        setIsMobileSidebarOpen={setIsMobileSidebarOpen}
        activeMenu={activeMenu}
        setActiveMenu={setActiveMenu}
        projectDetail={projectDetail}
      />

      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        <Topbar
          title="게시물 수정"
          subtitle={
            boardCodeFromUrl
              ? `${boardTitle} (${boardCodeFromUrl})`
              : '게시물 정보를 수정합니다'
          }
          isMobileSidebarOpen={isMobileSidebarOpen}
          setIsMobileSidebarOpen={setIsMobileSidebarOpen}
          isProfileDropdownOpen={isProfileDropdownOpen}
          setIsProfileDropdownOpen={setIsProfileDropdownOpen}
          showMobileLogo={true}
          projectDetail={projectDetail}
        />

        <main className="flex-1 p-4 sm:p-6 overflow-y-auto">
          <div className="max-w-4xl mx-auto">
            <div className="mb-6">
              <button
                type="button"
                onClick={() => navigate(viewPath)}
                className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900"
              >
                <ArrowLeft className="w-4 h-4" />
                게시물 보기로
              </button>
            </div>

            {isLoading ? (
              <div className="flex justify-center py-16">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
              </div>
            ) : loadError ? (
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                {loadError}
              </div>
            ) : !initialPost ? (
              <div className="text-center py-12 text-sm text-gray-500">
                게시물을 찾을 수 없습니다.
              </div>
            ) : (
              <form onSubmit={handleSubmit}>
                {submitError && (
                  <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                    {submitError}
                  </div>
                )}

                <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden mb-6">
                  <div className="px-4 lg:px-6 py-4 border-b border-gray-200">
                    <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                      <History className="w-5 h-5" />
                      게시물 정보
                    </h2>
                  </div>
                  <div className="px-4 lg:px-6 py-4 grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-gray-500">코드</span>
                      <p className="font-medium text-gray-900 mt-0.5">{initialPost.code}</p>
                    </div>
                    <div>
                      <span className="text-gray-500">상태</span>
                      <p className="font-medium text-gray-900 mt-0.5">{initialPost.status}</p>
                    </div>
                    <div>
                      <span className="text-gray-500">작성일</span>
                      <p className="font-medium text-gray-900 mt-0.5">
                        {formatDateTimeFromApi(initialPost.createdAt) || '—'}
                      </p>
                    </div>
                    {initialPost.updatedAt && (
                      <div>
                        <span className="text-gray-500">수정일</span>
                        <p className="font-medium text-gray-900 mt-0.5">
                          {formatDateTimeFromApi(initialPost.updatedAt)}
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                  <div className="px-4 lg:px-6 py-4 border-b border-gray-200">
                    <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                      <FileText className="w-5 h-5" />
                      수정 내용
                    </h2>
                  </div>
                  <div className="px-4 lg:px-6 py-2 space-y-0">
                    <FormRow label="제목" required error={errors.title}>
                      <input
                        type="text"
                        value={title}
                        onChange={(e) => {
                          setTitle(e.target.value);
                          setErrors((prev) => ({ ...prev, title: undefined }));
                        }}
                        placeholder="제목 (최대 100자)"
                        maxLength={100}
                        className={`block w-full min-w-[200px] px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                          errors.title ? 'border-red-500' : 'border-gray-300'
                        }`}
                      />
                    </FormRow>

                    <FormRow label="내용" required error={errors.content}>
                      <textarea
                        value={content}
                        onChange={(e) => {
                          setContent(e.target.value);
                          setErrors((prev) => ({ ...prev, content: undefined }));
                        }}
                        placeholder="게시물 내용을 입력하세요"
                        rows={12}
                        className={`block w-full min-w-[200px] px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-y ${
                          errors.content ? 'border-red-500' : 'border-gray-300'
                        }`}
                      />
                    </FormRow>

                    {fileUploadEnabled && (
                      <FormRow label="첨부 파일" hint={`upload · 최대 ${boardSettings.upload}개`}>
                        <p className="text-sm text-gray-500">
                          첨부 파일 변경은 현재 수정 API에서 지원하지 않습니다. 새 파일이 필요하면
                          게시물을 새로 등록해 주세요.
                        </p>
                      </FormRow>
                    )}
                  </div>
                </div>

                <div className="flex justify-between items-center mt-6">
                  <button
                    type="button"
                    onClick={handleReset}
                    className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center text-sm font-medium text-gray-700"
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    되돌리기
                  </button>
                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={() => navigate(viewPath)}
                      className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm font-medium text-gray-700"
                    >
                      취소
                    </button>
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center text-sm font-medium"
                    >
                      {isSubmitting ? '저장 중...' : '게시물 저장'}
                    </button>
                  </div>
                </div>
              </form>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default PostUpdate;
